<script setup lang="ts">
import { computed, ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Text from '@uiKit/elements/Text.vue'
import AppButton from '@uiKit/elements/Button.vue'
import EmptyState from '@uiKit/components/EmptyState.vue'
import { useInfiniteScrollObserver } from '@shared/composables/useInfiniteScrollObserver'
import TransactionListSkeleton from './TransactionListSkeleton.vue'
import TransactionRow from './TransactionRow.vue'
import type { Transaction } from '../types/transactionTypes'

interface Props {
  transactions: Transaction[]
  loading?: boolean
  loadingMore?: boolean
  hasMore?: boolean
  title?: string
  maxItems?: number
  selfScroll?: boolean
  emptyCtaLabel?: string
  showViewAll?: boolean
  showEmptyCta?: boolean
  // When true, the empty state reflects a filtered/searched view with zero
  // matches rather than a wallet that has never had any activity. Driven by
  // the parent — the list itself doesn't know about filters or search.
  isFiltered?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  loadingMore: false,
  hasMore: false,
  emptyCtaLabel: 'Add funds',
  showViewAll: true,
  showEmptyCta: true,
  isFiltered: false,
})

const visibleTransactions = computed(() =>
  props.maxItems ? props.transactions.slice(0, props.maxItems) : props.transactions,
)

const emit = defineEmits<{
  (e: 'view-all'): void
  (e: 'load-more'): void
  (e: 'view-details', tx: Transaction): void
  (e: 'empty-cta'): void
}>()

// Raw <div ref> needed — IntersectionObserver targets DOM nodes. §19 exception.
const listRef = ref<HTMLElement | null>(null)
const sentinelRef = ref<HTMLElement | null>(null)

useInfiniteScrollObserver(sentinelRef, {
  root: listRef,
  canLoadMore: () => Boolean(props.hasMore) && !props.loadingMore && !props.loading,
  onLoadMore: () => emit('load-more'),
})

const onRowViewDetails = (tx: Transaction) => {
  emit('view-details', tx)
}
</script>

<template>
  <Box direction="col" :class="['tl', { 'tl--self-scroll': props.selfScroll }]">
    <Box v-if="title" align="center" justify="between" class="tl__header">
      <Text type="page-title" class="tl__header-title">{{ title }}</Text>
      <AppButton
        v-if="showViewAll"
        label="View All"
        variant="link"
        color="primary"
        @click="emit('view-all')"
      />
    </Box>

    <Box v-if="!loading && visibleTransactions.length" class="tl__col-header">
      <Text type="body" weight="medium" is="div" class="tl__ch-cell tl__ch-cell--detail">
        Details
      </Text>
      <Text type="body" weight="medium" is="div" class="tl__ch-cell tl__ch-cell--date">
        Date & Time
      </Text>
      <Text type="body" weight="medium" is="div" class="tl__ch-cell tl__ch-cell--txnid">
        Reference ID
      </Text>
      <Text type="body" weight="medium" is="div" class="tl__ch-cell tl__ch-cell--amount">
        Amount
      </Text>
      <Text type="body" weight="medium" is="div" class="tl__ch-cell tl__ch-cell--status">
        Status
      </Text>
      <Text type="body" weight="medium" is="div" class="tl__ch-cell tl__ch-cell--view">
        Action
      </Text>
    </Box>

    <TransactionListSkeleton v-if="loading" />

    <EmptyState
      v-else-if="!visibleTransactions.length && isFiltered"
      size="compact"
      icon="search"
      title="No transactions match your filters"
      description="Try clearing a filter or widening the date range."
    />

    <EmptyState
      v-else-if="!visibleTransactions.length"
      size="compact"
      icon="list"
      title="No transactions yet"
      description="Your transaction history will appear here."
      :primary-action-label="showEmptyCta ? emptyCtaLabel : undefined"
      @primary-action="emit('empty-cta')"
    />

    <!-- Raw <div ref> for IntersectionObserver root. §19 exception. -->
    <div v-else ref="listRef" class="tl__list">
      <TransactionRow
        v-for="tx in visibleTransactions"
        :key="tx.id"
        :transaction="tx"
        @view-details="onRowViewDetails"
      />

      <!-- Raw <div ref> for IntersectionObserver target. §19 exception. -->
      <div v-if="hasMore || loadingMore" ref="sentinelRef" class="tl__sentinel">
        <Box v-if="loadingMore" align="center" justify="center" gap="2" class="tl__loading-more">
          <PiIcon name="spinner" class="tl__loading-more-icon" />
          <Text type="body-sm" color="muted" is="span">Loading more...</Text>
        </Box>
      </div>
    </div>

    <Box v-if="loadingMore" align="center" justify="center" class="tl__load-more">
      <PiIcon name="spinner" class="tl__spinner" />
    </Box>
  </Box>
</template>

<style scoped>
.tl__header {
  margin-bottom: 0.625rem;
}

.tl__header-title {
  letter-spacing: 0.1em;
}

.tl {
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  padding: 1.25rem;
  box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);
  box-sizing: border-box;
  width: 100%;
}

.tl--self-scroll {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  gap: 0;
}

.tl--self-scroll .tl__header {
  flex-shrink: 0;
  margin-bottom: 0.75rem;
}

.tl--self-scroll .tl__col-header {
  flex-shrink: 0;
  position: static;
  margin-bottom: 0.5rem;
}

.tl--self-scroll .tl__list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: var(--p-content-border-color) transparent;
}

.tl--self-scroll .tl__list::-webkit-scrollbar {
  width: 4px;
}

.tl__list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--p-content-border-color) transparent;
}

.tl__list > * {
  min-width: 0;
}

.tl__list::-webkit-scrollbar {
  height: 4px;
}

.tl__list::-webkit-scrollbar-track {
  background: transparent;
}

.tl__list::-webkit-scrollbar-thumb {
  background: var(--p-content-border-color);
  border-radius: 4px;
}

.tl__col-header {
  display: grid;
  grid-template-columns: minmax(220px, 2.4fr) minmax(120px, 1.4fr) minmax(150px, 1.6fr) minmax(110px, 1.2fr) minmax(110px, 1.2fr) minmax(100px, 1fr);
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--p-primary-50);
  border: 1px solid var(--p-primary-50);
  border-radius: 10px;
  padding: 0.5rem 1.25rem;
  height: 44px;
  box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);
  box-sizing: border-box;
  column-gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.tl__ch-cell {
  line-height: 1;
  white-space: nowrap;
  min-width: 0;
  text-align: left;
  color: var(--p-text-muted-color);
  letter-spacing: 0.02em;
}

.tl__ch-cell--detail {
  padding-right: 14px;
}

.tl__ch-cell--amount,
.tl__ch-cell--status,
.tl__ch-cell--view {
  text-align: center;
}

.tl__ch-cell--view {
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 992px) {
  .tl__col-header {
    display: none;
  }
}

.tl__sentinel {
  height: 1px;
}

.tl__loading-more {
  padding: 1rem 0;
  color: var(--p-text-muted-color);
}

.tl__loading-more-icon {
  font-size: 16px;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.tl__load-more {
  padding: 1rem 0 4px;
}

.tl__spinner {
  font-size: 22px;
  color: var(--p-primary-600);
  animation: spin 0.8s linear infinite;
}
</style>
